import React from "react";
import { Box, Divider, Link, Stack, Tooltip, Typography } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StatusChip from "../../RemediationHub/components/common/StatusChip";
import { formatRequestRef } from "../../RemediationHub/components/common/requestListHelpers";
import { buildRemediationHubUrl } from "../../RemediationHub/components/common/remediationHubNavigation";
import { formatAbsolute, formatRelative } from "../../RemediationHub/components/common/hooks";

const tooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
      border: "1px solid",
      borderColor: "divider",
      p: 1.5,
      maxWidth: 360,
    },
  },
};

const RemediationIcon = ({ size = 20 }) => (
  <BuildIcon sx={{ fontSize: size, color: "text.secondary" }} />
);

const DetailContent = ({ request }) => {
  if (!request) return null;
  const hubUrl = buildRemediationHubUrl(request.request_id);
  return (
    <Box sx={{ minWidth: 260 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Remediation in progress
      </Typography>
      <Stack spacing={1}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Task
          </Typography>
          <Link
            href={hubUrl}
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 600 }}
            onClick={(e) => e.stopPropagation()}
          >
            {formatRequestRef(request.request_id)}
          </Link>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Status
          </Typography>
          <StatusChip status={request.status} size="small" withTooltip={false} />
        </Box>
        {request.user_email && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Submitted by
            </Typography>
            <Typography variant="body2">{request.user_email}</Typography>
          </Box>
        )}
        {request.status_entered_at && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              In this status
            </Typography>
            <Typography variant="body2">
              {formatRelative(request.status_entered_at)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({formatAbsolute(request.status_entered_at)})
              </Typography>
            </Typography>
          </Box>
        )}
      </Stack>
      <Divider sx={{ my: 1.25 }} />
      <Link
        href={hubUrl}
        underline="hover"
        variant="caption"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
        onClick={(e) => e.stopPropagation()}
      >
        Open in Remediation Hub
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </Link>
    </Box>
  );
};

/** Wrench icon; hover for active-request details. */
export const ActiveRemediationHint = ({ request }) => {
  if (!request) return null;

  return (
    <Tooltip
      title={<DetailContent request={request} />}
      arrow
      placement="left"
      enterDelay={250}
      disableInteractive={false}
      slotProps={tooltipSlotProps}
    >
      <Box
        component="span"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          cursor: "default",
        }}
      >
        <RemediationIcon />
      </Box>
    </Tooltip>
  );
};

/** Shown in the expanded resource row when a fix is already filed. */
export const ActiveRemediationDetailPanel = ({ request }) => {
  if (!request) return null;
  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "#f8f9fa",
        border: "1px solid #e9ecef",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <DetailContent request={request} />
    </Box>
  );
};

export const RemediationStatusLegendIcon = () => <RemediationIcon />;

export const RemediationStatusLegend = () => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 2,
      py: 1.25,
      borderTop: "1px solid #e9ecef",
      backgroundColor: "#fafafa",
      borderRadius: "0 0 8px 8px",
    }}
  >
    <RemediationStatusLegendIcon />
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      This symbol indicates an active remediation task. Hover the icon for details.
    </Typography>
  </Stack>
);

export default ActiveRemediationHint;
